import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useInvoices, Invoice } from '../../../hooks/useInvoices';
import { useTheme } from '../../../contexts/ThemeContext';
import GenericListWidget from './GenericListWidget';

export interface PendingInvoicesListProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const PendingInvoicesList: React.FC<PendingInvoicesListProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { invoices = [], isLoading } = useInvoices(undefined, 'pending');
  const arr = Array.isArray(invoices) ? invoices : [];

  const items = React.useMemo(() => {
    const pending = arr.filter(
      (inv: Invoice) => !inv.invoiceIncome || inv.invoiceIncome === 0
    );
    return pending
      .sort((a: Invoice, b: Invoice) => {
        const da = new Date(a.invoiceDue || a.InvoiceDate || 0).getTime();
        const db = new Date(b.invoiceDue || b.InvoiceDate || 0).getTime();
        return da - db;
      })
      .slice(0, effectiveLimit);
  }, [arr, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  const formatCurrency = (v?: number) =>
    v != null ? `€${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';

  return (
    <GenericListWidget<Invoice>
      title="Pending Invoices"
      viewAllPath="/client-invoices"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No pending invoices"
      getItemKey={(inv) => inv.invoiceId}
      embedded={embedded}
      renderItem={(inv) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/client-invoices/${inv.invoiceId}`)}
          onKeyDown={(e) =>
            e.key === 'Enter' && navigate(`/client-invoices/${inv.invoiceId}`)
          }
          className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text}`}>
              {inv.InvoiceNo} {inv.client?.clientName && `· ${inv.client.clientName}`}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {formatCurrency(inv.invoiceAmount)}
              {inv.invoiceDue && ` · Due ${new Date(inv.invoiceDue).toLocaleDateString('en-US')}`}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default PendingInvoicesList;
